"use client";

import Image from "next/image";
import { useState } from "react";
import { Card, CardBody } from "@/components/ui/Card";
import { cn } from "@/lib/utils";

interface HomeTestimonialCardProps {
  quote: string;
  name: string;
  role: string;
  image: string;
  className?: string;
}

function InitialsAvatar({ name }: { name: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-primary/10 text-xs font-semibold text-primary">
      {name.charAt(0).toUpperCase() || "?"}
    </div>
  );
}

export function HomeTestimonialCard({
  quote,
  name,
  role,
  image,
  className,
}: HomeTestimonialCardProps) {
  const [imageError, setImageError] = useState(false);
  const isData = image.startsWith("data:");
  const hasImage = image.length > 0 && !imageError;

  return (
    <Card
      variant="interactive"
      className={cn(
        "overflow-hidden border-primary/15 bg-gradient-to-br from-[var(--card)] to-primary/[0.04]",
        className
      )}
    >
      <CardBody className="relative p-5 md:p-6">
        <span
          className="pointer-events-none absolute right-4 top-2 font-display text-6xl leading-none text-primary/15"
          aria-hidden
        >
          &ldquo;
        </span>
        <div className="flex gap-4">
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full ring-2 ring-primary/25 ring-offset-2 ring-offset-[var(--card)]">
            {hasImage ? (
              isData ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={image}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <Image
                  src={image}
                  alt=""
                  fill
                  className="object-cover"
                  sizes="56px"
                  onError={() => setImageError(true)}
                />
              )
            ) : (
              <InitialsAvatar name={name} />
            )}
          </div>
          <div className="min-w-0">
            <blockquote className="text-sm italic leading-relaxed text-foreground/90">
              &ldquo;{quote}&rdquo;
            </blockquote>
            <p className="mt-3 text-sm font-semibold text-foreground">{name}</p>
            <p className="text-xs text-muted">{role}</p>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
